import { useEffect, useState } from 'react'
import { listAssignableRoles, listUsers } from '@/api/users'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { UserFormCard } from '@/components/users/UserFormCard'
import { UserTableRow } from '@/components/users/UserTableRow'
import type { User, UserRole } from '@/api/types'

type StatusFilter = 'all' | 'active' | 'inactive'

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Alla' },
  { value: 'active', label: 'Aktiva' },
  { value: 'inactive', label: 'Inaktiva' }
]

function filterUsers(users: User[], filter: StatusFilter): User[] {
  if (filter === 'active') return users.filter((user) => user.active)
  if (filter === 'inactive') return users.filter((user) => !user.active)

  return users
}

export function UsersPage(): React.ReactElement {
  const currentUser = useAuthStore((state) => state.user)

  const [users, setUsers] = useState<User[]>([])
  const [roles, setRoles] = useState<UserRole[]>([])
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const isFormVisible = isCreating || editingUser !== null
  const visibleUsers = filterUsers(users, statusFilter)

  useEffect(() => {
    const load = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const [userList, roleList] = await Promise.all([listUsers(), listAssignableRoles()])

        setUsers(userList)
        setRoles(roleList)
      } catch {
        setErrorMessage('Kunde inte hämta användare just nu.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [])

  const handleStartCreating = (): void => {
    setEditingUser(null)
    setIsCreating(true)
  }

  const handleStartEditing = (user: User): void => {
    setIsCreating(false)
    setEditingUser(user)
  }

  const handleCancelForm = (): void => {
    setIsCreating(false)
    setEditingUser(null)
  }

  const handleUserSaved = (user: User): void => {
    setUsers((current) => {
      const exists = current.some((existing) => existing.id === user.id)

      return exists ? current.map((existing) => (existing.id === user.id ? user : existing)) : [...current, user]
    })
    setIsCreating(false)
    setEditingUser(null)
  }

  const handleUserChanged = (user: User): void => {
    setUsers((current) => current.map((existing) => (existing.id === user.id ? user : existing)))
  }

  if (!currentUser) {
    return <></>
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-none border-border/80">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="text-3xl font-serif text-primary">Användare</CardTitle>
            <CardDescription>Skapa, redigera, inaktivera och återaktivera personalkonton.</CardDescription>
          </div>
          {!isFormVisible && <Button onClick={handleStartCreating}>Ny användare</Button>}
        </CardHeader>
        <CardContent className="flex gap-2">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              type="button"
              variant={statusFilter === filter.value ? 'default' : 'outline'}
              size="sm"
              className="rounded-none"
              onClick={() => setStatusFilter(filter.value)}
            >
              {filter.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      {isFormVisible && (
        <UserFormCard
          user={editingUser}
          roles={roles}
          currentUserId={currentUser.id}
          onSaved={handleUserSaved}
          onCancel={handleCancelForm}
        />
      )}

      {errorMessage && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {!errorMessage && (
        <Card className="shadow-none border-border/80">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-3 pl-6 font-medium">Namn</th>
                    <th className="px-3 py-3 font-medium">E-post</th>
                    <th className="px-3 py-3 font-medium">Roll</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium">Skapad</th>
                    <th className="py-3 pl-3 pr-6 font-medium text-right">Åtgärder</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user) => (
                    <UserTableRow
                      key={user.id}
                      user={user}
                      currentUserId={currentUser.id}
                      onEdit={handleStartEditing}
                      onChanged={handleUserChanged}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {isLoading && <p className="px-6 py-8 text-sm text-muted-foreground">Laddar användare...</p>}
            {!isLoading && visibleUsers.length === 0 && <p className="px-6 py-8 text-sm text-muted-foreground">Inga användare hittades.</p>}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
